// src/hooks/useAnalytics.js
// Hook per tracciare eventi GA4 personalizzati (customer journey).
// Consent Mode (advanced): il tag GA è caricato sempre; senza consenso i cookie non sono
// impostati ma gli eventi partono come segnali senza cookie (descritto nella Privacy Policy).
//
// MAPPA EVENTI (aggiornata):
// ─ knowledge_base : article_open · article_read_complete · kb_filter_used · kb_search
// ─ tools          : tool_start · tool_csv_upload · simulation_run · fire_calculation
//                    fire_type_selected · wizard_start · wizard_completion
//                    mc_simulation_run · pdf_download · time_on_tool
//                    compound_mode_switched · compound_preset_selected
//                    compound_calculation · compound_advanced_opened
// ─ conversion     : cta_click · form_submit · modal_open
// ─ ux             : language_switch · form_validation_error · tab_switch

function gtag(...args) {
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

// Export di modulo: usabile anche fuori dai componenti React (es. negli
// oggetti JSX statici di Articles.it.js/Articles.en.js, dove gli hook non
// sono disponibili). Esposto anche dall'hook useAnalytics per coerenza.
export function trackVideoPlay(videoId, context) {
  gtag("event", "video_play", {
    event_category: "tools",
    video_id: videoId,
    video_context: context, // es. "article" | "ocf_pillar"
  });
}

export function useAnalytics() {

  // Page view SPA: inviato a ogni cambio rotta (send_page_view è false in index.html).
  // Consent Mode v2 blocca lo storage finché manca il consenso analytics.
  const trackPageView = (path, lang) => {
    gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      language: lang,
    });
  };

  // Articolo KB aperto
  const trackArticleOpen = (slug, lang) => {
    gtag("event", "article_open", {
      event_category: "knowledge_base",
      article_slug: slug,
      language: lang,
    });
  };

  // Articolo KB letto fino in fondo (chiamarlo quando lo scroll supera il 90%)
  const trackArticleRead = (slug, lang, readTimeSeconds) => {
    gtag("event", "article_read_complete", {
      event_category: "knowledge_base",
      article_slug: slug,
      language: lang,
      read_time_seconds: readTimeSeconds,
    });
  };

  // Filtro tag KB usato
  const trackKBFilter = (tag, lang) => {
    gtag("event", "kb_filter_used", {
      event_category: "knowledge_base",
      filter_tag: tag,
      language: lang,
    });
  };

  // Tool: simulatore avviato
  const trackToolStart = (toolName, lang) => {
    gtag("event", "tool_start", {
      event_category: "tools",
      tool_name: toolName,
      language: lang,
    });
  };

  // Tool: esercizio Risk Lab completato.
  // NOTA privacy (audit legale 12/07/2026): nessun parametro personale — né profilo-tipo
  // né soglia — per tenere fede al claim "le risposte e il risultato non vengono trasmessi".
  const trackRiskLabCompleted = (lang) => {
    gtag("event", "risk_lab_completed", {
      event_category: "tools",
      tool_name: "risk_lab",
      language: lang,
    });
  };

  // Tool: CSV caricato nel simulatore
  const trackToolCSVUpload = (tickerCount) => {
    gtag("event", "tool_csv_upload", {
      event_category: "tools",
      tool_name: "portfolio_simulator",
      ticker_count: tickerCount,
    });
  };

  // Tool: simulazione eseguita
  const trackSimulationRun = (tickerCount, years) => {
    gtag("event", "simulation_run", {
      event_category: "tools",
      tool_name: "portfolio_simulator",
      ticker_count: tickerCount,
      simulation_years: years,
    });
  };

  // Tool: PDF scaricato
  const trackPDFDownload = (toolName, lang) => {
    gtag("event", "pdf_download", {
      event_category: "tools",
      tool_name: toolName,
      language: lang,
    });
  };

  // CTA cliccato (Calendly, newsletter, YouTube, ecc.)
  const trackCTAClick = (ctaLabel, destination, siteMode) => {
    gtag("event", "cta_click", {
      event_category: "conversion",
      cta_label: ctaLabel,
      destination: destination,
      site_mode: siteMode,
    });
  };

  // Switch lingua
  const trackLanguageSwitch = (fromLang, toLang) => {
    gtag("event", "language_switch", {
      event_category: "ux",
      from_language: fromLang,
      to_language: toLang,
    });
  };

  // Ricerca nella KB
  const trackKBSearch = (query, lang, resultsCount) => {
    gtag("event", "kb_search", {
      event_category: "knowledge_base",
      search_query: query,
      language: lang,
      results_count: resultsCount,
    });
  };

  // FIRE Calculator: calcolo completato (risultati visibili all'utente)
  const trackFIRECalculation = (fireType, yearsToFire, lang) => {
    gtag("event", "fire_calculation", {
      event_category: "tools",
      tool_name: "fire_calculator",
      fire_type: fireType,
      years_to_fire: yearsToFire != null ? Math.round(yearsToFire) : null,
      language: lang,
    });
  };

  // FIRE Calculator: tipo FIRE selezionato (lean/regular/fat/barista/coast)
  const trackFireTypeSelected = (fireType, lang) => {
    gtag("event", "fire_type_selected", {
      event_category: "tools",
      tool_name: "fire_calculator",
      fire_type: fireType,
      language: lang,
    });
  };

  // FIRE Calculator: wizard spese aperto
  const trackWizardStart = (lang) => {
    gtag("event", "wizard_start", {
      event_category: "tools",
      tool_name: "fire_calculator",
      language: lang,
    });
  };

  // FIRE Calculator: wizard completato (utente ha premuto "Usa questo importo")
  const trackWizardCompletion = (annualAmount, lang) => {
    gtag("event", "wizard_completion", {
      event_category: "tools",
      tool_name: "fire_calculator",
      annual_amount_bucket: annualAmount < 15000 ? "<15k"
        : annualAmount < 30000 ? "15k-30k"
        : annualAmount < 60000 ? "30k-60k"
        : ">60k",
      language: lang,
    });
  };

  // FIRE Calculator: simulazione MC avviata
  const trackMCSimulationRun = (horizonYears, lang) => {
    gtag("event", "mc_simulation_run", {
      event_category: "tools",
      tool_name: "fire_calculator",
      horizon_years: horizonYears,
      language: lang,
    });
  };

  // Tool: modale aperta (contact_form | mailing_list)
  const trackModalOpen = (modalName, context, lang) => {
    gtag("event", "modal_open", {
      event_category: "conversion",
      modal_name: modalName,
      open_context: context,   // es. "hero", "about", "navbar", "hash"
      language: lang,
    });
  };

  // Form: invio tentato (success = true|false)
  const trackFormSubmit = (formName, success, reason) => {
    gtag("event", "form_submit", {
      event_category: "conversion",
      form_name: formName,     // "contact_form" | "mailing_list"
      success,
      contact_reason: reason || null,  // solo per contact_form
    });
  };

  // Form: errore di validazione mostrato all'utente
  const trackFormValidationError = (formName, errorType) => {
    gtag("event", "form_validation_error", {
      event_category: "ux",
      form_name: formName,
      error_type: errorType,   // "missing_fields" | "missing_gdpr"
    });
  };

  // ToolsPage: cambio tab (etf → fire o vice versa)
  const trackTabSwitch = (fromTab, toTab, lang) => {
    gtag("event", "tab_switch", {
      event_category: "tools",
      from_tab: fromTab,
      to_tab: toTab,
      language: lang,
    });
  };

  // Tempo di utilizzo tool (secondi, al dismount del componente)
  const trackTimeOnTool = (toolName, seconds, lang) => {
    if (seconds < 5) return;  // ignora sessioni sotto soglia (bounce / prerender)
    gtag("event", "time_on_tool", {
      event_category: "tools",
      tool_name: toolName,
      duration_seconds: seconds,
      duration_bucket: seconds < 30 ? "<30s"
        : seconds < 120 ? "30s-2m"
        : seconds < 300 ? "2m-5m"
        : ">5m",
      language: lang,
    });
  };

  // ── Compound Goal Calculator events ─────────────────────────────────────

  // Mode switched between A (versamento→risultato) and B (obiettivo→rata)
  const trackCompoundModeSwitched = (fromMode, toMode, lang) => {
    gtag("event", "compound_mode_switched", {
      event_category: "tools",
      tool_name: "compound_goal_calculator",
      from_mode: fromMode,
      to_mode: toMode,
      language: lang,
    });
  };

  // Preset chip selected (home, education, wedding, car, emergency, custom)
  const trackCompoundPresetSelected = (presetId, lang) => {
    gtag("event", "compound_preset_selected", {
      event_category: "tools",
      tool_name: "compound_goal_calculator",
      preset_id: presetId,
      language: lang,
    });
  };

  // Calculation completed (debounced 1.5s to avoid keystroke spam)
  const trackCompoundCalculation = (mode, presetId, horizonBucket, lang) => {
    gtag("event", "compound_calculation", {
      event_category: "tools",
      tool_name: "compound_goal_calculator",
      calc_mode: mode,            // "A" | "B"
      preset_id: presetId,
      horizon_bucket: horizonBucket,  // "≤5y" | "6-10y" | "11-20y" | ">20y"
      language: lang,
    });
  };

  // Advanced settings panel opened
  const trackCompoundAdvancedOpened = (lang) => {
    gtag("event", "compound_advanced_opened", {
      event_category: "tools",
      tool_name: "compound_goal_calculator",
      language: lang,
    });
  };

  return {
    trackPageView,
    trackVideoPlay,
    trackArticleOpen,
    trackArticleRead,
    trackKBFilter,
    trackToolStart,
    trackRiskLabCompleted,
    trackToolCSVUpload,
    trackSimulationRun,
    trackPDFDownload,
    trackCTAClick,
    trackLanguageSwitch,
    trackKBSearch,
    trackFIRECalculation,
    trackFireTypeSelected,
    trackWizardStart,
    trackWizardCompletion,
    trackMCSimulationRun,
    trackModalOpen,
    trackFormSubmit,
    trackFormValidationError,
    trackTabSwitch,
    trackTimeOnTool,
    trackCompoundModeSwitched,
    trackCompoundPresetSelected,
    trackCompoundCalculation,
    trackCompoundAdvancedOpened,
  };
}
